/**
 * La scheda di punteggio **bersaglio per bersaglio**, sul modello PractiScore.
 *
 * È la forma che il riferimento del settore usa da sempre, ed è anche quella
 * del regolamento: una riga per bersaglio (T1, T2, …), una cella per zona, si
 * **tocca per incrementare**. Due Alpha su T1 sono due tocchi su A — nessuna
 * tastiera numerica, che al poligono col telefono è la differenza fra
 * registrare e rimandare.
 *
 * Quattro comportamenti presi dal riferimento, ognuno per un motivo:
 *
 *  - **tocco sull'etichetta T1 azzera la riga** — correggere senza cancellare
 *    carattere per carattere;
 *  - **riga verde quando il bersaglio è completo, rossa quando ha troppi
 *    impatti** — è il punto 26, risolto *mostrandolo*. Il cap viene dal
 *    regolamento: due migliori impatti sulla carta (FITDS 9.5.1), massimo due
 *    penalità no-shoot per bersaglio (9.4.2 e 9.4.3);
 *  - **il metallo si conta al contrario** — un popper di norma cade, quindi
 *    nasce colpito e si tocca per segnarlo in piedi;
 *  - **niente blocchi duri.** Un impatto in più si segnala, non si impedisce:
 *    un dato strano va mostrato, o al poligono si perde la registrazione.
 *
 * Spec: docs/shooting/13-piano-revisione-test-federico.md §Gruppo D
 */
import { Check } from "lucide-react";
import {
	assignTargetNumbers,
	defaultHitsRequired,
	elementSpec,
} from "./stageElements";

/** Massimo di penalità no-shoot per bersaglio (FITDS 9.4.2 e 9.4.3). */
const MAX_NO_SHOOT_PENALTIES = 2;

const sum = (counts) =>
	Object.values(counts || {}).reduce((total, n) => total + (Number(n) || 0), 0);

/**
 * I bersagli da scorare: solo quelli che si sparano, già numerati.
 *
 * I no-shoot restano fuori dalla scheda — il tiratore non deve cercare un T4
 * che non deve compilare — ma le loro penalità si registrano sul bersaglio
 * che si stava ingaggiando, che è come funziona davvero.
 */
export const scorableTargets = (targets) =>
	assignTargetNumbers(targets || [])
		.filter((target) => defaultHitsRequired(target.target_type) > 0)
		// Un gruppo `3× IPSC` è tre bersagli da scorare, non uno.
		.flatMap((target) =>
			Array.from({ length: target.count || 1 }, (_, i) => ({
				...target,
				count: 1,
				target_no: (target.target_no || 1) + i,
			})),
		)
		.map((target, index) => ({ ...target, target_no: target.target_no ?? index + 1 }));

export function TargetScoreCard({
	targets,
	zones,
	penalties,
	value = [],
	onChange,
	courseType = "comstock",
}) {
	const list = scorableTargets(targets);
	const byNo = new Map(value.map((row) => [row.target_no, row]));

	const rowFor = (no) => byNo.get(no) || { target_no: no, hits: {}, penalties: {} };

	const write = (no, next) => {
		const others = value.filter((row) => row.target_no !== no);
		const isEmpty = sum(next.hits) === 0 && sum(next.penalties) === 0;
		onChange(isEmpty ? others : [...others, next]);
	};

	const bump = (no, group, code, delta) => {
		const row = rowFor(no);
		const counts = { ...(row[group] || {}) };
		const next = Math.max(0, (counts[code] || 0) + delta);
		if (next === 0) delete counts[code];
		else counts[code] = next;
		write(no, { ...row, [group]: counts });
	};

	const clearRow = (no) => write(no, { target_no: no, hits: {}, penalties: {} });

	if (list.length === 0) return null;

	return (
		<div className="space-y-1">
			{/* Intestazioni: le celle sono quadrati numerici, e senza il codice
			    sopra non si sa quale sia A e quale D. */}
			<div
				className="grid gap-1 px-1"
				style={{
					gridTemplateColumns: `2.2rem repeat(${zones.length + penalties.length}, minmax(0, 1fr))`,
				}}
			>
				<span />
				{zones.map((zone) => (
					<span
						key={zone.code}
						className="text-[10px] text-center text-muted-foreground"
					>
						{zone.code}
					</span>
				))}
				{penalties.map((penalty) => (
					<span
						key={penalty.code}
						className="text-[10px] text-center text-red-400/80"
					>
						{penalty.code}
					</span>
				))}
			</div>

			{list.map((target) => {
				const spec = elementSpec(target.target_type);
				const row = rowFor(target.target_no);
				const required =
					target.hits_required ?? defaultHitsRequired(target.target_type);
				const scored = sum(row.hits);
				const isSteel = spec.family === "steel";
				// Comstock ammette colpi liberi, ma gli **impatti che contano**
				// restano quelli: oltre, la riga è rossa.
				const tooMany =
					scored > required ||
					sum(row.penalties) > MAX_NO_SHOOT_PENALTIES * 2;
				const complete = scored === required && !tooMany;

				return (
					<div
						key={target.target_no}
						className={`grid gap-1 items-center rounded-sm border px-1 py-1 transition-colors ${
							tooMany
								? "border-red-500/60 bg-red-500/10"
								: complete
									? "border-volt/50 bg-volt/10"
									: "border-border/60 bg-secondary/30"
						}`}
						style={{
							gridTemplateColumns: `2.2rem repeat(${zones.length + penalties.length}, minmax(0, 1fr))`,
						}}
					>
						<button
							type="button"
							onClick={() => clearRow(target.target_no)}
							title={`${spec.label} — tocca per azzerare`}
							className="h-10 rounded-sm text-[11px] font-semibold text-muted-foreground hover:text-red-400 flex flex-col items-center justify-center leading-none"
						>
							<span>T{target.target_no}</span>
							<span className="text-[9px] opacity-60 font-normal">
								{isSteel ? "⬤" : `×${required}`}
							</span>
						</button>

						{zones.map((zone) => {
							const count = row.hits?.[zone.code] || 0;
							return (
								<button
									key={zone.code}
									type="button"
									onClick={() => bump(target.target_no, "hits", zone.code, 1)}
									onContextMenu={(e) => {
										e.preventDefault();
										bump(target.target_no, "hits", zone.code, -1);
									}}
									className={`h-10 rounded-sm text-base font-medium tabular-nums transition-colors ${
										count > 0
											? "bg-volt/20 text-volt"
											: "bg-background text-muted-foreground/40 hover:bg-background/60"
									}`}
									aria-label={`T${target.target_no} zona ${zone.code}: ${count}`}
								>
									{count || "·"}
								</button>
							);
						})}

						{penalties.map((penalty) => {
							const count = row.penalties?.[penalty.code] || 0;
							return (
								<button
									key={penalty.code}
									type="button"
									onClick={() =>
										bump(target.target_no, "penalties", penalty.code, 1)
									}
									onContextMenu={(e) => {
										e.preventDefault();
										bump(target.target_no, "penalties", penalty.code, -1);
									}}
									className={`h-10 rounded-sm text-base font-medium tabular-nums transition-colors ${
										count > 0
											? "bg-red-500/20 text-red-400"
											: "bg-background text-muted-foreground/40 hover:bg-background/60"
									}`}
									aria-label={`T${target.target_no} penalità ${penalty.code}: ${count}`}
								>
									{count || "·"}
								</button>
							);
						})}
					</div>
				);
			})}
		</div>
	);
}

/**
 * Stato della scheda: quanti bersagli sono completi, e se è pronta.
 *
 * È il campo "Ready" del riferimento — dice se si può salvare prima di
 * scoprirlo dopo. Puro, così è testabile.
 */
export const targetCardStatus = (targets, rows = []) => {
	const list = scorableTargets(targets);
	if (list.length === 0) return null;
	const byNo = new Map(rows.map((row) => [row.target_no, row]));
	let done = 0;
	let over = 0;
	for (const target of list) {
		const row = byNo.get(target.target_no);
		const scored = sum(row?.hits);
		const required =
			target.hits_required ?? defaultHitsRequired(target.target_type);
		if (scored > required) over += 1;
		else if (scored === required) done += 1;
	}
	return { total: list.length, done, over, ready: done === list.length };
};

export function TargetCardStatusLine({ status }) {
	if (!status) return null;
	if (status.over > 0) {
		return (
			<p className="text-[11px] text-red-400">
				{status.over} bersagli con più impatti di quanti ne contino.
			</p>
		);
	}
	return (
		<p
			className={`text-[11px] flex items-center gap-1 ${
				status.ready ? "text-volt" : "text-muted-foreground"
			}`}
		>
			{status.ready && <Check className="w-3 h-3" />}
			{status.ready
				? "Scheda completa"
				: `${status.done} di ${status.total} bersagli scorati`}
		</p>
	);
}
